import { getClientConfig } from '@arcanumai/client-config';
import { awsCredentialsService } from './awsCredentialsService';
import { portalAdmin } from './portalAdmin';

/**
 * Platform-level settings stored as a special "platform-settings" record
 * in the numa-client-config DynamoDB table. This is NOT a real client —
 * it holds global defaults that apply across all customers.
 */

export interface PlatformSettings {
  /** Existing min interval (minutes) — Level 1 floor. */
  schedulingMinIntervalMinutes?: number;
  /** Hard ceiling on scheduled (cron) runs per company per month. */
  maxRunsPerCompanyPerMonth?: number;
  /** Cap on scheduled (cron) runs per user per month. Above this triggers admin approval. */
  maxRunsPerUserPerMonth?: number;
  /** Cap on event-trigger fires per company per month (actuals). */
  maxTriggerRunsPerCompanyPerMonth?: number;
  /** Cap on event-trigger fires per user per month (actuals). */
  maxTriggerRunsPerUserPerMonth?: number;
  /** Cap on simultaneously active automations (cron + triggers) across the tenant. */
  maxConcurrentActiveSchedulesPerCompany?: number;
  /** Cap on simultaneously active automations per user (cron + triggers). */
  maxConcurrentActiveSchedulesPerUser?: number;
  /** If true, schedules above the user cap are routed to admin approval. Default true. */
  requireApprovalAboveUserCap?: boolean;
}

const PLATFORM_SETTINGS_KEY = 'platform-settings';

/**
 * Bootstrap seed values, used ONLY when the platform-settings record in
 * DynamoDB is missing or has incomplete fields (e.g. a fresh deployer
 * account that has never opened the Platform Settings page). The runtime
 * authority is the DynamoDB record itself — these constants are not used
 * once the record is fully populated.
 *
 * The values mirror what the original PLATFORM_DEFAULT_QUOTAS in
 * `lib/schedule-load.ts` used to fall back to. They no longer exist there
 * because lambdas now throw if the record is incomplete (fail loud rather
 * than silently masking a misconfiguration).
 */
export const PLATFORM_QUOTA_INITIAL_VALUES = {
  schedulingMinIntervalMinutes: 60,
  maxRunsPerCompanyPerMonth: 2_000,
  maxRunsPerUserPerMonth: 750,
  maxTriggerRunsPerCompanyPerMonth: 1_000,
  maxTriggerRunsPerUserPerMonth: 100,
  maxConcurrentActiveSchedulesPerCompany: 1_000,
  maxConcurrentActiveSchedulesPerUser: 100,
  requireApprovalAboveUserCap: true,
} as const;

export const platformSettingsService = {
  async get(): Promise<PlatformSettings> {
    try {
      const credentials = awsCredentialsService.credentialsIfConfigured();
      const config = await awsCredentialsService.retryIfRevoked(() =>
        getClientConfig<PlatformSettings>({
          clientName: PLATFORM_SETTINGS_KEY,
          credentials,
        })
      );
      return config ?? {};
    } catch {
      // Record may not exist yet
      return {};
    }
  },

  /**
   * Saves the record through the portal-admin Lambda (TASK-630), which logs
   * the change. An empty field on the form means "inherit platform default",
   * which is what an absent attribute gives, so undefined values are dropped.
   */
  async save(settings: PlatformSettings): Promise<void> {
    const cleaned = Object.fromEntries(Object.entries(settings).filter(([, v]) => v !== undefined)) as PlatformSettings;
    await portalAdmin('clientConfig.savePlatformSettings', { settings: cleaned });
  },
};
